// La pestaña «todas las líneas»: una fila por LÍNEA de venta, con su desglose.
//
// Las otras dos pestañas enseñan una fila por (línea × factura), que es el grano
// que devuelve la vista. Aquí la fila es la LÍNEA y el desglose cuelga debajo.
//
// LA CLAVE DE AGRUPACIÓN ES `inventtransid`, NUNCA `numLinea`. Medido sobre la
// vista: 0 filas sin `inventtransid` en 556.607 —vale como clave para todas—, pero
// 11.018 pedidos donde un mismo `numLinea` cubre varias líneas distintas, porque hay
// pedidos importados con toda la numeración repetida. Agrupar por el número fusiona
// líneas que no son la misma, y no da error: da una fila con cantidades sumadas que
// parece correcta. La columna «#» enseñará números repetidos en esos pedidos; es el
// dato real y va explicado en su tooltip.
//
// CUÁNDO HAY CHEVRON. Sólo cuando el desglose no cabe en la propia fila. Con una
// línea servida en una entrega y facturada de una vez —el 99,2 % de las líneas, y no
// es una estimación: 546.955 de 551.556— la factura, el albarán y la carga caben en
// la fila padre y abrir no enseñaría nada nuevo. Un chevron que abre una fila para
// enseñar una fila entrena al usuario a no pulsarlo, justo antes de llegar al 1 %
// donde sí había algo. Mismo criterio que la barra de picking, que no se pinta al
// 100 %, y que la columna de cargas, que resuelve el caso simple con un «+N».
import type { MarcasFilas, PedidoVentaLinea } from '@/types'
import type { ResumenAlbaranes } from './albaranesLinea'
import { claveFila } from './marcasFilas'

export interface LineaAgrupada {
  /** Clave de agrupación y clave de fila. */
  inventtransid: string
  /** La primera fila de la línea. Sus campos de NIVEL LÍNEA —`qtyLinea`,
   *  `qtySinFacturar`, artículo, familia, estado— son constantes en todas sus
   *  filas, así que sirve de cabecera sin calcular nada. */
  cabecera: PedidoVentaLinea
  /** Todas las filas de la línea: una por factura, más la del remanente. */
  filas: PedidoVentaLinea[]
  /** Facturas distintas de la línea (sin contar la fila remanente). */
  facturas: string[]
  /** Albaranes distintos, de todas sus filas. */
  albaranes: string[]
  /** Cargas distintas, de todas sus filas. Vienen del albarán, así que son las
   *  cargas en las que REALMENTE salió — no todas las de la línea. */
  cargas: string[]
  /** ¿Hay algo que el padre no pueda enseñar por sí solo? Decide el chevron. */
  tieneDesglose: boolean
}

interface Contexto {
  /** Los albaranes que le tocan a una fila, con sus cargas dentro.
   *  `albaranesLinea.ts` es quien sabe la regla; aquí sólo se consulta. */
  albaranesDe: (l: PedidoVentaLinea) => ResumenAlbaranes | undefined
}

function unicos(valores: (string | undefined)[]): string[] {
  return [...new Set(valores.filter(Boolean) as string[])].sort()
}

/**
 * Agrupa las filas de un pedido por línea de venta, conservando el orden en que
 * llegaron (que ya viene ordenado por `num_linea` desde el backend).
 *
 * Las filas sin `inventtransid` se descartan: sin clave no hay línea a la que
 * pertenecer, y hoy no hay ninguna. Si algún día aparecen, desaparecer de esta
 * pestaña es preferible a inventarles un grupo propio que el usuario leería como
 * una línea más del pedido.
 */
export function agruparLineas(
  lineas: PedidoVentaLinea[],
  { albaranesDe }: Contexto,
): LineaAgrupada[] {
  const porLinea = new Map<string, PedidoVentaLinea[]>()
  for (const l of lineas) {
    const k = l.inventtransid
    if (!k) continue
    const arr = porLinea.get(k)
    if (arr) arr.push(l)
    else porLinea.set(k, [l])
  }

  return [...porLinea.entries()].map(([inventtransid, filas]) => {
    const facturas = unicos(filas.map(f => f.factura?.trim() || undefined))
    const albaranes = unicos(filas.flatMap(f => albaranesDe(f)?.albaranes ?? []))
    const cargas = unicos(filas.flatMap(f => albaranesDe(f)?.cargas ?? []))

    return {
      inventtransid,
      cabecera: filas[0],
      filas,
      facturas,
      albaranes,
      cargas,
      // Con una sola fila y un solo albarán, todo cabe arriba. En cuanto hay dos
      // de cualquiera de las dos cosas, la fila ya no puede contarlo entero.
      tieneDesglose: filas.length > 1 || albaranes.length > 1,
    }
  })
}

/**
 * Las claves de fila de las OTRAS dos pestañas que pertenecen a esta línea.
 *
 * Existe por las marcas del usuario. Allí la fila es (línea × factura) y aquí es la
 * línea, así que las claves no coinciden; pero marcar en esta pestaña significa
 * «me interesa este artículo», no «me interesa esta factura», y el usuario espera
 * verlo marcado al cambiar de pestaña. El mapeo vive aquí, en un solo sitio, para
 * que no se reimplemente medio distinto en cada pantalla.
 */
export function clavesDeFilaDe(grupo: LineaAgrupada): string[] {
  // Idéntica a la que construye `TablaLineas` para esas pestañas. Si allí cambia,
  // aquí también: son la misma clave vista desde los dos lados.
  return grupo.filas.map(f => claveFila(f.inventtransid ?? f.numLinea ?? '', f.factura ?? ''))
}

/**
 * Envuelve el objeto de marcas para que marcar la fila de una LÍNEA marque también
 * sus filas en las otras dos pestañas.
 *
 * Se envuelve el objeto en vez de tocar `MarcaCelda` o `FilaMarcable` a propósito:
 * esos dos los usan seis pantallas y ninguna necesita esto. Aquí la fila representa
 * varias filas de otro grano, que es un problema de ESTA pantalla.
 *
 * `tiene` exige que TODAS las hijas estén marcadas, y `alternar` escribe en todas.
 * Que sea «todas» y no «la primera» es lo que permite marcar una sublínea suelta
 * sin que la línea entera se encienda: son dos gestos distintos —«me interesa este
 * artículo» y «me interesa esta entrega»— y tienen que poder convivir.
 *
 * Lo demás pasa tal cual: el ámbito, el recuento y el «limpiar» son del pedido
 * entero y no cambian.
 */
export function marcasDeLinea(
  marcas: MarcasFilas,
  clavesDe: (clave: string) => string[],
): MarcasFilas {
  return {
    ambito: marcas.ambito,
    tiene: (clave) => {
      const claves = clavesDe(clave)
      return claves.length > 0 ? claves.every(k => marcas.tiene(k)) : marcas.tiene(clave)
    },
    total: () => marcas.total(),
    todas: () => marcas.todas(),
    alternar: (clave) => {
      const claves = clavesDe(clave)
      if (claves.length === 0) return marcas.alternar(clave)
      // Se decide UNA vez, por la primera, y se aplica a todas. Llamar a
      // `alternar` en cada una desmarcaría las que ya estuvieran marcadas y
      // dejaría la línea a medias.
      if (marcas.tiene(claves[0])) claves.forEach(k => marcas.quitar(k))
      else claves.forEach(k => { if (!marcas.tiene(k)) marcas.alternar(k) })
    },
    quitar: clave => clavesDe(clave).forEach(k => marcas.quitar(k)),
    limpiar: () => marcas.limpiar(),
    // A CUALQUIER cambio: el estado de la línea depende de todas sus hijas, así
    // que suscribirse sólo a una la dejaría desactualizada al marcar otra.
    suscribir: (_clave, alCambiar) => marcas.suscribir(null, alCambiar),
  }
}
